import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { ExportDropdown } from "@components/common/export/export-dropdown";
import {
  ExportHandlerOptions,
  ExportResult,
} from "@components/common/export/export-dropdown-types";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { Markup } from "@custom-types/project-markups-types";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { GUID } from "@faro-lotv/ielement-types";
import { addIElements } from "@faro-lotv/project-source";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import { SettingsApiTypes } from "@stellar/settings-api-client";
import { worldTransformCacheSelector } from "@store/i-elements/i-elements-selectors";
import { markupsSelector } from "@store/markups/markups-selector";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { store } from "@store/store";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { downloadMarkupData, ExportFormat } from "@utils/file-data-export";
import { TableType } from "@utils/track-event/track-event-list";
import { useCallback } from "react";

interface Props extends BaseProjectIdProps {
  /** Ids of the markups to export */
  markupIds: GUID[];

  /** The name of the markup's project */
  projectName: string;

  /** True if some entities are selected */
  areEntitiesSelected: boolean;
}

/** Dropdown to export markups in the file format selected by the user */
export function BulkExportMarkups({
  projectId,
  markupIds,
  projectName,
  areEntitiesSelected,
}: Props): JSX.Element {
  const { companyMembers, projectMembers } = useMembers();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { formatDate } = useDateTime();
  const projectApiClient = useProjectApiClient({ projectId });
  const dispatch = useAppDispatch();
  const userSettings = useAppSelector(userSettingsSelector);
  const unitMeasurement: SettingsApiTypes.UnitMeasurement =
    userSettings.unit_measurement?.value ?? "meters";

  /** Returns the url of the markup opening either in Viewer or WebEditor */
  const markupHref = useCallback(
    (markup: Markup) => {
      let openTarget = ProjectLaunchTarget.sphereViewer;
      if (
        projectLaunchSettings.defaultOpenProjectTarget ===
          ProjectLaunchTarget.webEditor &&
        markup?.webEditorUrl
      ) {
        openTarget = projectLaunchSettings.defaultOpenProjectTarget;
      }

      return openTarget === ProjectLaunchTarget.sphereViewer
        ? markup.sphereViewerUrl
        : markup.webEditorUrl;
    },
    [projectLaunchSettings.defaultOpenProjectTarget]
  );

  /**
   * Fetches the additional data required for exporting the markups,
   * which is not fetched by default when fetching markups for the table.
   * The additional data includes the markup area (sheet) and markup capture (scene)
   * @param markupIds - The ids of the markups to fetch the additional data for
   * @param options - The export handler options, including the abort signal
   * @returns a promise that resolves when the additional data has been fetched and set in the store
   */
  const fetchMarkupsAdditionalData = useCallback(
    async (
      markupIds: GUID[],
      options?: ExportHandlerOptions
    ): Promise<void> => {
      const ancestors = await projectApiClient.getAllIElements({
        descendantIds: markupIds,
        signal: options?.signal,
      });
      dispatch(addIElements(ancestors));
    },
    [dispatch, projectApiClient]
  );

  /**
   * Handler function to export the markups in the selected file format.
   * It first fetches the additional data required for export,
   * and then downloads the markup data in the specified format.
   * @param format - The file format to export the data in (CSV, XLSX, JSON)
   * @param options - The export handler options, including the abort signal and onProgress callback
   * @returns a promise that resolves to the result of the export operation
   */
  const exportHandler = useCallback(
    async (
      format: ExportFormat,
      options?: ExportHandlerOptions
    ): Promise<ExportResult> => {
      await fetchMarkupsAdditionalData(markupIds, options);

      if (options?.signal?.aborted) {
        return null;
      }

      // After fetching the additional export data of the markups, use the
      // store object directly instead of through the useAppSelector hook.
      // This ensures that we get the most up-to-date markups data with the
      // additional data required for export.
      const updatedMarkups = markupsSelector(store.getState());
      const selectedMarkups = updatedMarkups.filter((markup) =>
        markupIds.includes(markup.id)
      );
      const worldTransformCache = worldTransformCacheSelector(store.getState());

      return await downloadMarkupData({
        markups: selectedMarkups,
        companyMembers,
        projectMembers,
        fileName: `${projectName}-Annotation List`,
        markupHref,
        formatDate,
        format,
        worldTransformCache,
        unitMeasurement,
      });
    },
    [
      fetchMarkupsAdditionalData,
      markupIds,
      projectName,
      companyMembers,
      projectMembers,
      markupHref,
      formatDate,
      unitMeasurement,
    ]
  );

  return (
    <ExportDropdown
      trackingProps={{
        dataType: TableType.projectMarkups,
        numberOfEntities: markupIds.length,
        areEntitiesSelected,
      }}
      exportHandler={exportHandler}
      toastMessage={{
        success: "Successfully exported annotations.",
        error: "Cannot export annotations. Please try again.",
      }}
      buttonTooltipText="Export annotations"
      shouldShowExportAllDialog
    />
  );
}
